import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  InputSignal,
  model,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { DeleteFileRequest, Files, InvestigationResultDto, Tags } from '../../../interfaces';
import { CaseService } from '../../../services/case.service';
import { DialogModule } from 'primeng/dialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { ButtonModule } from 'primeng/button';
import { TranslatePipe } from '@ngx-translate/core';
import { TextareaModule } from 'primeng/textarea';
import { InvestigationResultModalComponent } from "../investigation-result-modal/investigation-result-modal.component";
import { Subject, takeUntil } from 'rxjs';
import { ChipComponent } from '../../chip/chip.component';
import { API_URL, Roles } from '../../../utils';
import { AuthService } from '../../../services';

@Component({
  selector: 'app-investigation-results',
  standalone: true,
  imports: [ChipComponent, FormsModule, DialogModule,
    MultiSelectModule, ButtonModule, TranslatePipe,
    TextareaModule, InvestigationResultModalComponent],
  templateUrl: './investigation-results.component.html',
  styleUrl: './investigation-results.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvestigationResultsComponent implements OnChanges {

  investigationResults = model.required<InvestigationResultDto[]>();
  caseId: InputSignal<string> = input.required();
  claimStatus: InputSignal<string> = input.required();
  @Input({ required: true }) investigationStatuses: any;
  apiUrl = inject(API_URL);
  @Output() openModal = new EventEmitter<number>();
  @Output() selectedFiles: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectedTagChange = new EventEmitter<string | null>();
  @Input() savedAmount!: number | null;
  @Output() savedAmountChange = new EventEmitter<number>();

  selectedInvestigationResult: WritableSignal<InvestigationResultDto> = signal({
    comment: '',
    investigationResultId: '',
    investigator: '',
    visibilityStatus: '',
    tags: [],
    files: []
  });


  showInvestigationResultModal = signal<boolean>(false);

  expandedRowIndex: number | null = null;
  isSubmitDisabled = false;

  private ngUnsubscribe = new Subject();

  private authService = inject(AuthService);
  private caseService = inject(CaseService);
  private cdr = inject(ChangeDetectorRef)
  constructor() { }
  localFile = signal<File | null>(null);

  get isCaseDetailReadOnly(): boolean {
    const role = this.authService.currentUserValue?.role;
    return role === Roles.Adjuster || role === Roles.Guest || role === Roles.Secretary;
  }

  ngOnChanges(changes: SimpleChanges): void { }

  toggleExpandRow(rowIndex: number): void {
    if (this.expandedRowIndex === rowIndex) {
      this.expandedRowIndex = null;
    } else {
      this.expandedRowIndex = rowIndex;
    }
  }

  updateVisibilityStatusToPermanent(comment: InvestigationResultDto) {
    if (this.isCaseDetailReadOnly) {
      return;
    }

    this.caseService
      .updateInvestigationStatus(comment.investigationResultId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.investigationResults.update(results =>
            results.map(r =>
              r.investigationResultId === comment.investigationResultId
                ? { ...r, visibilityStatus: 'Permanent' }
                : r
            )
          );
          this.cdr.markForCheck();
        }
      })
  }


  removeTag(tagId: number) {
    if (this.isCaseDetailReadOnly) {
      return;
    }

    if (!this.selectedInvestigationResult()) {
      return;
    }

    this.caseService
      .deleteInvestigationResultTag(
        this.selectedInvestigationResult().investigationResultId,
        tagId
      ).pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.selectedInvestigationResult.update(current => ({
            ...current,
            tags: current.tags.filter((tag: any) => tag.id !== tagId)
          }));

          this.investigationResults.update((prevResults) =>
            prevResults.map((result) =>
              result.investigationResultId ===
                this.selectedInvestigationResult().investigationResultId
                ? {
                  ...result,
                  tags: result.tags.filter((tag: any) => tag.id !== tagId),
                }
                : result
            )
          );
        },
      });
    this.cdr.markForCheck();
  }
  submitEdit(payload: { result: InvestigationResultDto; commentDeleted?: boolean } | InvestigationResultDto) {
    if (this.isCaseDetailReadOnly) {
      return;
    }

    const updatedResult = 'result' in payload ? payload.result : payload;
    const commentDeleted = 'result' in payload ? !!payload.commentDeleted : false;

    const newFile = this.localFile();

    if (newFile) {

      const original = this.investigationResults().find(
        r => r.investigationResultId === updatedResult.investigationResultId
      )!;

      const oldFile = original.files?.[0];

      if (oldFile) {
        const deleteReq: DeleteFileRequest = {
          fileName: oldFile.fileName,
          filePath: oldFile.blobUri
        };

        this.caseService.deleteInvestigationResultFile(deleteReq)
          .subscribe({
            next: () => console.log("Old file deleted"),
            error: err => console.error("Error deleting old file", err)
          });
      }

      const formData = new FormData();
      formData.append('CaseId', this.caseId());
      formData.append('InvestigationResultId', updatedResult.investigationResultId);
      formData.append('File', newFile);

      this.caseService.uploadFile(formData)
        .subscribe({
          next: () => {
            console.log("New file uploaded");

            updatedResult.files = [{
              fileName: newFile.name,
              blobUri: `${this.caseId()}/${updatedResult.investigator}/`,
              isError: false
            }];
          },
          error: err => console.error("Upload failed", err)
        });
    }

    const original = this.investigationResults().find(
      r => r.investigationResultId === updatedResult.investigationResultId
    )!;

    if (!commentDeleted && updatedResult.comment !== original.comment) {
      this.caseService
        .updateInvestigationResultComment(
          updatedResult.investigationResultId,
          updatedResult.comment ?? ''
        )
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: () => {
            this.investigationResults.update(prev =>
              prev.map(r =>
                r.investigationResultId === updatedResult.investigationResultId
                  ? { ...r, comment: updatedResult.comment }
                  : r
              )
            );
            this.cdr.markForCheck();
          },
        });
    }

    if (commentDeleted) {
      this.caseService
        .deleteInvestigationResultComment(updatedResult.investigationResultId)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: () => {
            this.investigationResults.update(prev =>
              prev.map(r =>
                r.investigationResultId === updatedResult.investigationResultId
                  ? { ...r, comment: '' }
                  : r
              )
            );
            this.cdr.markForCheck();
          },
        });
    }

    const removed = original.tags.filter(o => !updatedResult.tags.find(u => u.id === o.id));
    const added = updatedResult.tags.filter(u => !original.tags.find(o => o.id === u.id));

    removed.forEach(tag =>
      this.removeTag(tag.id)
    );

    if (added.length > 0) {
      this.addInvestigationResultTag({ tags: added });
    }

    this.investigationResults.update(prev =>
      prev.map(r =>
        r.investigationResultId === updatedResult.investigationResultId
          ? updatedResult
          : r
      )
    );
    const newTag = updatedResult.tags?.[0]?.name ?? null;
    this.selectedTagChange.emit(newTag);

    this.showInvestigationResultModal.set(false);
    this.localFile.set(null);
    this.cdr.markForCheck();
  }

  deleteCommentById(investigationResultId: string): void {
    if (this.isCaseDetailReadOnly) {
      return;
    }

    const target = this.investigationResults().find(
      r => r.investigationResultId === investigationResultId
    );

    if (!target) {
      return;
    }

    this.deleteComment(target);
  }

  deleteComment(comment: InvestigationResultDto): void {
    this.caseService
      .deleteInvestigationResultComment(comment.investigationResultId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.investigationResults.update(prev =>
            prev.map(r =>
              r.investigationResultId === comment.investigationResultId
                ? { ...r, comment: '' }
                : r
            )
          );
          this.cdr.markForCheck();
        },
      });
  }

  addInvestigationResultTag(event: any) {
    if (this.isCaseDetailReadOnly) {
      return;
    }

    const addTag = event.tags.map((tag: any) => tag.name ?? tag);

    this.caseService
      .addInvestigationResultTag(
        this.selectedInvestigationResult().investigationResultId,
        addTag
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          const combinedTags = [
            ...(this.selectedInvestigationResult().tags || []),
            ...(event.tags || []),
          ];

          const uniqueTags = Array.from(
            new Set(combinedTags.map((tag: any) => tag.name))
          ).map((name: string) =>
            combinedTags.find((tag: any) => tag.name === name)
          ) as Tags[];

          this.selectedInvestigationResult.update(current => ({
            ...current,
            tags: uniqueTags
          }));

          this.investigationResults.update(prevResults =>
            prevResults.map(result =>
              result.investigationResultId ===
                this.selectedInvestigationResult().investigationResultId
                ? { ...result, tags: uniqueTags }
                : result
            )
          );

          this.cdr.markForCheck();
          this.showInvestigationResultModal.set(false);

        },
      });
  }

  onRemoveFile(file: Files) {

    if (!file || !file.fileName || !file.blobUri) {
      return;
    }

    const fileToBeDeleted: DeleteFileRequest = {
      fileName: file.fileName,
      filePath: file.blobUri,
    };
    this.caseService.deleteInvestigationResultFile(fileToBeDeleted)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: () => {
          this.selectedInvestigationResult.update(current => ({
            ...current,
            files: current.files.filter(result => result.fileName !== fileToBeDeleted.fileName)
          }));
        },
        error: (err) => {
          console.error('Error deleting file:', err);
        }
      });
  }

  uploadFiles(files: File[]) {
    if (!files.length) return;

    this.localFile.set(files[0]);
  }


  openComments(comment: InvestigationResultDto) {
    if (this.isCaseDetailReadOnly) {
      return;
    }

    const cloned = {
      ...comment,
      tags: [...comment.tags],
      files: [...comment.files]
    };

    this.selectedInvestigationResult.set(cloned);

    this.showInvestigationResultModal.set(true);
  }

  getImage(file: any) {
    let re = /(?:\.([^.]+))?$/;
    if (re.exec(file.fileName) == null) {
      return '';
    }
    if (re.exec(file.fileName)![1] == 'txt') {
      return './assets/icons/txt.png';
    } else if (re.exec(file.fileName)![1] == 'docx') {
      return './assets/icons/docx.png';
    } else {
      return './assets/icons/pdf.png';
    }
  }

  downloadFile(file: Files) {

    this.caseService
      .downloadCaseFiles(this.caseId(), file.fileName).pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (blob: Blob) => {
          const downloadLink = document.createElement('a');
          const objectUrl = URL.createObjectURL(blob);
          downloadLink.href = objectUrl;
          downloadLink.download = file.fileName;
          downloadLink.click();
          URL.revokeObjectURL(objectUrl);
        },
        error: (error) => {
          console.error('File download failed', error);
        },
      });
  }

  updateSavedAmount(amount: number) {
    if (amount == null) return;
    this.savedAmountChange.emit(amount);
  }

  private updateUploadedFiles(files: any[]) {
    if (!Array.isArray(files) || !files.length) return;

    const file = files[0];

    this.selectedInvestigationResult.update(current => ({
      ...current,
      files: [file]
    }));

    this.investigationResults.update((results) =>
      results.map((result) =>
        result.investigationResultId === this.selectedInvestigationResult().investigationResultId
          ? { ...result, files: [file] }
          : result
      )
    );

    this.cdr.markForCheck();
  }
}
